import { gql } from "@codesandbox/api";
import { signal, effect, cleanup } from "impact-react";
import { router } from "router";

import type { EditorStoreProps } from "../types";

import {
  createLiveSessionMutation,
  stopLiveSessionMutation,
  subscribeLiveSession,
  getLiveSessionQuery,
  setGuestPermission,
  setDefaultPermission,
} from "./queries";
import type { LiveSession } from "./queries";

type BaseState = {
  defaultPermission: gql.LiveSessionPermission;
  notifyWhenGuestsJoin: boolean;
  role: "HOST" | "GUEST";
};

export type State = BaseState &
  (
    | { status: "IDLE"; prevSession?: LiveSession }
    | { status: "CREATING" }
    | { status: "CONNECTED"; session: LiveSession }
    | { status: "ERROR"; error: string }
  );

export function useLiveSession(props: EditorStoreProps) {
  const {
    liveSessionInitialState: initialState,

    api,
    track,
    editor,
    session,
    pitcher,
    followChannel,
    addStatusNotification,
  } = props;

  /**
   * States
   */
  const state = signal<State>(
    initialState
      ? {
          status: "CONNECTED",
          session: initialState,
          defaultPermission: initialState.defaultPermission,
          notifyWhenGuestsJoin: true,
          /**
           * At this point it's sage to assume it's a guest
           * because only guest can have an initial state,
           * otherwise the host will fetch the current session
           * bellow in this same feature
           */
          role: "GUEST",
        }
      : {
          status: "IDLE",
          defaultPermission: gql.LiveSessionPermission.READ,
          notifyWhenGuestsJoin: true,
          role: "HOST",
        },
  );
  const guestPermission = signal(state.value.defaultPermission);

  /**
   * Constants
   */
  const sandboxId = editor.id;
  const isHost = state.value.role === "HOST";

  effect(function watchGuestPermission() {
    let permission = state.value.defaultPermission;

    if (
      state.value.role === "GUEST" &&
      state.value.status === "CONNECTED" &&
      session.state === "AUTHENTICATED"
    ) {
      const guest = state.value.session.guests.find(
        (g) => g.userId === session.user.id,
      );

      if (guest) {
        permission = guest.permission;
      }
    }

    guestPermission.value = permission;
  });

  const clients = signal(pitcher.clients.client.getClients());
  const disposableClientUpdate = pitcher.clients.client.onClientsUpdated(
    (newClients) => {
      clients.value = newClients;
    },
  );

  effect(function followHost() {
    if (state.value.status !== "CONNECTED" || isHost) {
      return;
    }

    const hostUserName = state.value.session.host?.username;
    if (hostUserName) {
      const client = clients.value.find(
        (client) => client.username === hostUserName,
      );

      if (client && guestPermission.value === "READ") {
        followChannel?.follow(client.clientId);
      }
    }
  });

  effect(function connectedEffects() {
    if (state.value.status !== "CONNECTED") {
      return;
    }

    return api.gql.subscribe(
      subscribeLiveSession,
      ({ liveSessionEvents: { session } }) => {
        if (state.value.status !== "CONNECTED") {
          return;
        }
        /**
         * Stop session for guests
         */
        if (session.stoppedAt) {
          pitcher.disconnect();

          router.push("liveEnd", { vmId: state.value.session.vmId });

          return;
        }

        if (state.value.status !== "CONNECTED") {
          return;
        }

        /**
         * Notify interface when there's a new user
         */
        const oldGuestsIds = state.value.session.guests.map((g) => g.userId);
        const newUser = session.guests.find(
          (g) => !oldGuestsIds.includes(g.userId),
        );

        if (newUser) {
          track("live_session_guest_joined", {
            numberOfGuests: session.guests.length,
          });

          if (state.value.notifyWhenGuestsJoin) {
            addStatusNotification({
              status: "info",
              message: `${newUser.username} joined the live session`,
            });
          }
        }

        /**
         * Notify guests when theirs permission changes
         */
        const prevGuestPermission = guestPermission.value;

        state.value = {
          ...state.value,
          status: "CONNECTED",
          session,
        };

        if (prevGuestPermission !== guestPermission.value && !isHost) {
          addStatusNotification({
            status: "info",
            message:
              guestPermission.value === "READ"
                ? "Host has changed your permission to read-only"
                : "Host has changed your permission to editor",
          });

          pitcher.reconnect();
        }
      },
      { sandboxId },
    );
  });

  // Get initial live session state
  if (isHost) {
    api.gql.query(getLiveSessionQuery, { sandboxId }).then((data) => {
      if (data.getLiveSession) {
        state.value = {
          ...state.value,
          status: "CONNECTED",
          session: data.getLiveSession,
        };
      }
    });
  }

  cleanup(() => {
    disposableClientUpdate.dispose();
  });

  return {
    get state() {
      return state.value;
    },
    get guestPermission() {
      return guestPermission.value;
    },
    async start() {
      if (state.value.status !== "IDLE") {
        return;
      }

      state.value = {
        ...state.value,
        status: "CREATING",
      };

      try {
        const { startLiveSession: session } = await api.gql.query(
          createLiveSessionMutation,
          {
            defaultPermission: state.value.defaultPermission,
            sandboxId,
          },
        );

        track("live_session_started");

        state.value = {
          ...state.value,
          status: "CONNECTED",
          session,
        };
      } catch (error) {
        state.value = {
          ...state.value,
          status: "ERROR",
          error: String(error),
        };
      }
    },
    stop() {
      if (state.value.status !== "CONNECTED") {
        return;
      }

      const prevState = state.value;

      try {
        api.gql.query(stopLiveSessionMutation, { sandboxId });

        track("live_session_stopped");

        state.value = {
          ...state.value,
          status: "IDLE",
          prevSession: state.value.session,
        };
      } catch {
        state.value = prevState;

        addStatusNotification({
          status: "error",
          message: "Could not stop the session, try again.",
        });
      }
    },
    setNotifyWhenGuestsJoin(value: boolean) {
      state.value = {
        ...state.value,
        notifyWhenGuestsJoin: value,
      };
    },
    async setDefaultPermision(permission: gql.LiveSessionPermission) {
      const prevPermission = state.value.defaultPermission;

      // Opmistic update
      state.value = {
        ...state.value,
        defaultPermission: permission,
      };

      if (state.value.status !== "CONNECTED") {
        return;
      }

      try {
        api.gql.query(setDefaultPermission, { permission, sandboxId });

        track("live_session_default_permission_changed");
      } catch {
        state.value = {
          ...state.value,
          defaultPermission: prevPermission,
        };

        addStatusNotification({
          status: "error",
          message:
            "Could not change default permission of Live Session, try again.",
        });
      }
    },
    setGuestPermission(props: {
      userId: string;
      permission: gql.LiveSessionPermission;
    }) {
      if (state.value.status !== "CONNECTED") {
        return;
      }

      track("live_session_guest_permission_changed");

      const prevPermission = state.value.session.guests;

      // Opmistic update
      state.value = {
        ...state.value,
        session: {
          ...state.value.session,
          guests: state.value.session.guests.map((g) =>
            g.userId === props.userId
              ? { ...g, permission: props.permission }
              : g,
          ),
        },
      };

      api.gql.query(setGuestPermission, { ...props, sandboxId }).catch(() => {
        if (state.value.status !== "CONNECTED") {
          return;
        }

        state.value = {
          ...state.value,
          session: {
            ...state.value.session,
            guests: prevPermission,
          },
        };

        addStatusNotification({
          status: "error",
          message: "Could not change guest permission, try again",
        });
      });
    },
  };
}
